import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import api from "@/utils/api";
import { appApiEndpoint } from "@/environment";
import { flattenTree } from "@/utils/helpers";
import log from "loglevel";
import sortBy from "lodash/sortBy";

export const createJobClip = createAsyncThunk(
  "jobs/createJobClip",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const payload = arg.payload;

    const withCredentials = true;
    try {
      const response = await api.post(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-clips`,
        {},
        payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateJobClip = createAsyncThunk(
  "jobs/updateJobClip",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobClipPermalink = arg.jobClipPermalink;
    const payload = arg.payload;

    const withCredentials = true;
    try {
      const response = await api.put(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-clips/${jobClipPermalink}`,
        {},
        payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const duplicateJobClip = createAsyncThunk(
  "jobs/duplicateJobClip",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobClipPermalink = arg.jobClipPermalink;
    const payload = arg.payload;

    const withCredentials = true;
    try {
      const response = await api.post(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-clips/${jobClipPermalink}/duplicate`,
        {},
        payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loadPublicJobClip = createAsyncThunk(
  "jobs/loadPublicJobClip",
  async (arg, thunkAPI) => {
    const withCredentials = false;
    try {
      const response = await api.get(
        withCredentials,
        `${appApiEndpoint}/v1/public/job-clips/${arg}`,
        {},
        {},
        null,
        false
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loadPublicJobPage = createAsyncThunk(
  "jobs/loadPublicJobPage",
  async (arg, thunkAPI) => {
    const jobBoardPermalink = arg.jobBoardPermalink;
    const jobPagePermalink = arg.jobPagePermalink;

    const withCredentials = false;
    try {
      const response = await api.get(
        withCredentials,
        `${appApiEndpoint}/v1/public/job-boards/${jobBoardPermalink}/jobs/${jobPagePermalink}`,
        {},
        {},
        null,
        false
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loadJobClips = createAsyncThunk(
  "jobs/loadJobClips",
  async (arg, thunkAPI) => {
    const withCredentials = true;
    try {
      const response = await api.get(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${arg}/job-clips?perPage=1000`,
        {}
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loadJobClipsPaginated = createAsyncThunk(
  "jobs/loadJobClipsPaginated",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const perPage = arg.perPage;
    const cursor = arg.cursor;
    const searchQuery = arg.searchQuery;
    const includedStates = arg.includedStates;

    let url = `${appApiEndpoint}/v1/employers/${employerPermalink}/job-clips?perPage=${perPage}`;
    if (cursor) {
      url += `&cursor=${cursor}`;
    }

    if (searchQuery) {
      url += `&search=${searchQuery}`;
    }

    if (includedStates && includedStates.length > 0) {
      includedStates.forEach((state) => {
        url += `&includedStates[]=${state}`;
      });
    }

    const withCredentials = true;
    try {
      const response = await api.get(withCredentials, url, {});
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loadJobClip = createAsyncThunk(
  "jobs/loadJobClip",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobClipPermalink = arg.jobClipPermalink;

    const withCredentials = true;
    try {
      const response = await api.get(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-clips/${jobClipPermalink}`,
        {}
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loadJobClipMetrics = createAsyncThunk(
  "jobs/loadJobClipMetrics",
  async (arg, thunkAPI) => {
    const withCredentials = true;
    try {
      const response = await api.get(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${arg}/job-clips/metrics`,
        {}
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getJobClipFromJobClipPermalink = (jobClipPermalink, state) => {
  const { jobs } = state;

  return jobs?.myJobClips?.byPermalink[jobClipPermalink];
};

export const loadPublicJobBoard = createAsyncThunk(
  "jobs/loadPublicJobBoard",
  async (arg, thunkAPI) => {
    const withCredentials = false;
    try {
      const jobBoard = await api.get(
        withCredentials,
        `${appApiEndpoint}/v1/public/job-boards/${arg}`,
        {},
        {},
        null,
        false
      );

      return augmentJobBoard(jobBoard);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loadJobBoards = createAsyncThunk(
  "jobs/loadJobBoards",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobBoardPermalinks = arg.jobBoardPermalinks;
    const jobPageStates = arg.jobPageStates ?? ["public", "private"];

    const withCredentials = true;
    try {
      // Make a separate call for each job board and return the results
      const jobBoards = await Promise.all(
        jobBoardPermalinks.map((jobBoardPermalink) => {
          let url = new URL(
            `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}`
          );
          let params = new URLSearchParams();
          params.append("perPage", 1000);
          jobPageStates.forEach((state) => {
            params.append("jobPageStates[]", state);
          });
          url.search = params.toString();

          return api.get(withCredentials, url.toString());
        })
      );

      return jobBoards.map((jobBoard) => augmentJobBoard(jobBoard));
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const addHeartToJobClipHighlightPublic = createAsyncThunk(
  "jobs/addHeartToJobClipHighlightPublic",
  async (arg, thunkAPI) => {
    const withCredentials = false;
    try {
      const response = await api.post(
        withCredentials,
        `${appApiEndpoint}/v1/public/job-clip-highlights/${arg}/addHeart`
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loadJobPage = createAsyncThunk(
  "jobs/loadJobPage",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobPagePermalink = arg.jobPagePermalink;

    const withCredentials = true;
    try {
      const response = await api.get(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-pages/${jobPagePermalink}`,
        {}
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createJobPage = createAsyncThunk(
  "jobs/createJobPage",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const payload = arg.payload;

    const withCredentials = true;
    try {
      const response = await api.post(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-pages`,
        {},
        payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateJobPage = createAsyncThunk(
  "jobs/updateJobPage",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobPagePermalink = arg.jobPagePermalink;
    const payload = arg.payload;

    const withCredentials = true;
    try {
      const response = await api.put(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-pages/${jobPagePermalink}`,
        {},
        payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loadJobBoardJobs = createAsyncThunk(
  "jobs/loadJobBoardJobs",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobBoardPermalink = arg.jobBoardPermalink;

    const withCredentials = true;
    try {
      const response = await api.get(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/jobs`
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const configureFreeTrial = createAsyncThunk(
  "jobs/configureFreeTrial",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobBoardPermalink = arg.jobBoardPermalink;
    const payload = arg.payload;

    const withCredentials = true;
    try {
      const response = await api.put(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/configure/free-trial`,
        {},
        payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const loadJobBoardJobDepartments = createAsyncThunk(
  "jobs/loadJobBoardJobDepartments",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobBoardPermalink = arg.jobBoardPermalink;

    let url = new URL(
      `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-departments`
    );

    let params = new URLSearchParams();
    params.append("perPage", 1000);

    url.search = params.toString();

    const withCredentials = true;
    try {
      const response = await api.get(withCredentials, url);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createJobBoardJobDepartment = createAsyncThunk(
  "jobs/createJobBoardJobDepartment",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobBoardPermalink = arg.jobBoardPermalink;
    const payload = arg.payload;

    const withCredentials = true;
    try {
      const response = await api.post(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-departments`,
        {},
        payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateJobBoardJobDepartment = createAsyncThunk(
  "jobs/updateJobBoardJobDepartment",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobBoardPermalink = arg.jobBoardPermalink;
    const jobDepartmentPermalink = arg.jobDepartmentPermalink;
    const payload = arg.payload;

    const withCredentials = true;
    try {
      const response = await api.put(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-departments/${jobDepartmentPermalink}`,
        {},
        payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);
export const loadJobBoardJobOffices = createAsyncThunk(
  "jobs/loadJobBoardJobOffices",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobBoardPermalink = arg.jobBoardPermalink;

    let url = new URL(
      `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-offices`
    );

    let params = new URLSearchParams();
    params.append("perPage", 1000);

    url.search = params.toString();

    const withCredentials = true;
    try {
      const response = await api.get(withCredentials, url);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const createJobBoardJobOffice = createAsyncThunk(
  "jobs/createJobBoardJobOffice",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobBoardPermalink = arg.jobBoardPermalink;
    const payload = arg.payload;

    const withCredentials = true;
    try {
      const response = await api.post(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-offices`,
        {},
        payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const updateJobBoardJobOffice = createAsyncThunk(
  "jobs/updateJobBoardJobOffice",
  async (arg, thunkAPI) => {
    const employerPermalink = arg.employerPermalink;
    const jobBoardPermalink = arg.jobBoardPermalink;
    const jobOfficePermalink = arg.jobOfficePermalink;
    const payload = arg.payload;

    const withCredentials = true;
    try {
      const response = await api.put(
        withCredentials,
        `${appApiEndpoint}/v1/employers/${employerPermalink}/job-boards/${jobBoardPermalink}/job-offices/${jobOfficePermalink}`,
        {},
        payload
      );
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const augmentJobBoard = (jobBoard) => {
  // Create 2-deep list of nested departments, for rendering the job-row
  jobBoard.flatDepartments = jobBoard.departments.map((department) => {
    return {
      ...department,
      flatChildren: flattenTree(
        department.children.map((c) => {
          return {
            ...c,
            parent: department,
          };
        }),
        (root) => {
          return root.children;
        }
      ),
    };
  });

  // Create 2-deep list of nested offices, for rendering the job-row
  jobBoard.flatOffices = jobBoard.offices.map((office) => {
    return {
      ...office,
      flatChildren: flattenTree(
        office.children.map((c) => {
          return {
            ...c,
            parent: office,
          };
        }),
        (root) => {
          return root.children;
        }
      ),
    };
  });

  // Add top-level department and office to each job, for filtering
  jobBoard.jobs = jobBoard.jobs.map((job) => {
    const jobDepartmentPermalinks = job.departments.map((d) => d.permalink);
    const jobOfficePermalinks = job.offices.map((o) => o.permalink);
    return {
      ...job,
      topDepartment: jobBoard.flatDepartments.find((top) => {
        if (jobDepartmentPermalinks.includes(top.permalink)) return top;
        return !!top.flatChildren.find((c) =>
          jobDepartmentPermalinks.includes(c.permalink)
        );
      }),
      topOffice: jobBoard.flatOffices.find((top) => {
        if (jobOfficePermalinks.includes(top.permalink)) return top;
        return !!top.flatChildren.find((c) =>
          jobOfficePermalinks.includes(c.permalink)
        );
      }),
    };
  });

  // Add jobCount to top-level departments and flatChildren
  jobBoard.flatDepartments = jobBoard.flatDepartments.map((department) => {
    const flatChildren = department.flatChildren.map((child) => {
      // Check each department in the departments array
      const jobCount = jobBoard.jobs.filter((j) => {
        return j.departments.find((d) => d.permalink === child.permalink);
      }).length;

      return {
        ...child,
        jobCount,
      };
    });

    return {
      ...department,
      jobCount:
        jobBoard.jobs.filter((j) =>
          j.departments.map((d) => d.permalink).includes(department.permalink)
        ).length + flatChildren.reduce((acc, c) => acc + c.jobCount, 0),
      flatChildren,
    };
  });

  // Add jobCount to top-level offices and flatChildren
  jobBoard.flatOffices = jobBoard.flatOffices.map((office) => {
    const flatChildren = office.flatChildren.map((child) => {
      // Check each office in the offices array
      const jobCount = jobBoard.jobs.filter((j) => {
        return j.offices.find((d) => d.permalink === child.permalink);
      }).length;

      return {
        ...child,
        jobCount,
      };
    });

    return {
      ...office,
      jobCount:
        jobBoard.jobs.filter((j) =>
          j.offices.map((o) => o.permalink).includes(office.permalink)
        ).length + flatChildren.reduce((acc, c) => acc + c.jobCount, 0),
      flatChildren,
    };
  });

  // log.debug(jobBoard.flatDepartments);
  // log.debug(jobBoard.flatOffices);
  // log.debug(jobBoard.jobs);

  return jobBoard;
};

// An entity being either a department or location
export const getFlatEntitiesWithJobs = (flatEntities) => {
  let flatEntitiesWithJobs = flatEntities.filter((d) => d.jobCount > 0);
  // Filter out any empty sub-departments
  flatEntitiesWithJobs = flatEntitiesWithJobs.map((d) => {
    let flatChildren = d.flatChildren.filter((child) => child.jobCount > 0);
    flatChildren = sortBy(flatChildren, "name");
    return {
      ...d,
      flatChildren,
    };
  });
  flatEntitiesWithJobs = sortBy(flatEntitiesWithJobs, "name");

  return flatEntitiesWithJobs;
};

export const initialState = {
  savingJobClip: false,
  saveJobClipError: false,
  saveJobClipSuccess: false,
  loadingPublicJobClip: false,
  loadingJobClips: false,
  loadingPublicJobBoard: false,
};

const jobsSlice = createSlice({
  name: "jobs",
  initialState: initialState,
  reducers: {
    clearFlags(state) {
      state = { ...state, ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createJobClip.pending, (state, action) => {
        state["savingJobClip"] = false;
        state["saveJobClipSuccess"] = false;
        state["saveJobClipError"] = false;
      })
      .addCase(createJobClip.fulfilled, (state, action) => {
        const jobClip = action.payload;

        state["myJobClips"].byPermalink[jobClip.permalink] = jobClip;
        state["myJobClips"].allPermalinks.push(jobClip.permalink);

        state["savingJobClip"] = false;
        state["saveJobClipSuccess"] = true;
        state["saveJobClipError"] = false;
      })
      .addCase(createJobClip.rejected, (state, action) => {
        state["savingJobClip"] = false;
        state["saveJobClipSuccess"] = false;
        state["saveJobClipError"] = true;
      })
      .addCase(updateJobClip.pending, (state, action) => {
        state["savingJobClip"] = false;
        state["saveJobClipSuccess"] = false;
        state["saveJobClipError"] = false;
      })
      .addCase(updateJobClip.fulfilled, (state, action) => {
        const jobClip = action.payload;

        if (state["myJobClips"]) {
          state["myJobClips"].byPermalink[jobClip.permalink] = jobClip;
        }

        state["savingJobClip"] = false;
        state["saveJobClipSuccess"] = true;
        state["saveJobClipError"] = false;
      })
      .addCase(updateJobClip.rejected, (state, action) => {
        state["savingJobClip"] = false;
        state["saveJobClipSuccess"] = false;
        state["saveJobClipError"] = true;
      })
      .addCase(duplicateJobClip.pending, (state, action) => {
        state["savingJobClip"] = false;
        state["saveJobClipSuccess"] = false;
        state["saveJobClipError"] = false;
      })
      .addCase(duplicateJobClip.fulfilled, (state, action) => {
        const jobClip = action.payload;

        state["myJobClips"].allPermalinks.push(jobClip.permalink);
        state["myJobClips"].byPermalink[jobClip.permalink] = jobClip;

        state["savingJobClip"] = false;
        state["saveJobClipSuccess"] = true;
        state["saveJobClipError"] = false;
      })
      .addCase(duplicateJobClip.rejected, (state, action) => {
        state["savingJobClip"] = false;
        state["saveJobClipSuccess"] = false;
        state["saveJobClipError"] = true;
      })
      .addCase(loadPublicJobClip.pending, (state, action) => {
        state["loadingPublicJobClip"] = true;
      })
      .addCase(loadPublicJobClip.fulfilled, (state, action) => {
        state["loadingPublicJobClip"] = false;
      })
      .addCase(loadPublicJobClip.rejected, (state, action) => {
        state["loadingPublicJobClip"] = false;
      })
      .addCase(loadJobClips.pending, (state, action) => {
        state["loadingJobClips"] = true;
      })
      .addCase(loadJobClips.fulfilled, (state, action) => {
        state["myJobClips"] = {
          byPermalink: {},
          allPermalinks: [],
        };
        action.payload.records.forEach((jobClip) => {
          state["myJobClips"].byPermalink[jobClip.permalink] = jobClip;
          state["myJobClips"].allPermalinks.push(jobClip.permalink);
        });

        state["loadingJobClips"] = false;
      })
      .addCase(loadJobClips.rejected, (state, action) => {
        state["loadingJobClips"] = false;
      })
      .addMatcher(
        (action) => action.type === "auth/logout/fulfilled",
        (state, action) => {
          return initialState;
        }
      );
  },
});

export const { clearFlags } = jobsSlice.actions;

export default jobsSlice.reducer;
